/**
 * Schuljahr-Zugehörigkeit eines Kindes (#1346).
 *
 * Bestätigen Eltern die Verlängerung, springt `contract.currentSchoolYear` sofort
 * auf das neue Schuljahr — der Vertrag des laufenden Schuljahres läuft aber noch
 * bis zum 31.07. Ein verlängertes Kind zählt deshalb bis dahin in BEIDEN
 * Schuljahren. Erkannt wird die Verlängerung am `history`-Eintrag, den
 * `renewStudentContract` für das neue Schuljahr anlegt; neue Verträge starten mit
 * leerer History und zählen nur in ihrem Schuljahr.
 *
 * Doppelt gehalten (Frontend-Lib und functions/src/_shared), weil die Functions
 * die Nx-Libraries nicht importieren können. Beide Kopien werden in
 * apps/parity-tests/student-school-year.parity.spec.ts gegeneinander geprüft —
 * Änderungen immer in BEIDEN Dateien.
 */

/** Minimaler Ausschnitt eines Students, den die Zuordnung braucht. */
export interface StudentSchoolYearInput {
  yearInfo?: { year?: number } | null;
  contract?: {
    currentSchoolYear?: number;
    history?: Array<{ schoolYear: string }> | null;
  } | null;
}

/** Schuljahr, dem das Kind laut Vertrag bzw. Anmeldung aktuell zugeordnet ist. */
export function getStudentSchoolYear(
  student: StudentSchoolYearInput
): number | undefined {
  return student.contract?.currentSchoolYear || student.yearInfo?.year;
}

/**
 * Gehört das Kind zum Schuljahr `schoolYear`?
 *
 * Zusätzlich zum aktuellen Schuljahr gilt: wurde der Vertrag in das Folgejahr
 * verlängert, gehört das Kind bis einschließlich 31.07. (UTC) des Folgejahres
 * auch noch zu `schoolYear`.
 */
export function isStudentInSchoolYear(
  student: StudentSchoolYearInput,
  schoolYear: number,
  now: Date = new Date()
): boolean {
  const current = getStudentSchoolYear(student);
  if (current === schoolYear) return true;

  // Auch mehrfach verlängerte Kinder (Folgejahr oder weiter): der History-Eintrag
  // für schoolYear + 1 belegt, dass der Vertrag in schoolYear lief.
  if (current === undefined || current <= schoolYear) return false;
  const renewed = !!student.contract?.history?.some(
    (entry) => entry.schoolYear === String(schoolYear + 1)
  );
  return renewed && now.getTime() < Date.UTC(schoolYear + 1, 7, 1);
}
